import React from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useOrganization } from '../context/OrganizationContext';

const ADMIN_EMAILS = ['shim.razilov@gmail.com'];

interface SidebarProps {
  isOpen: boolean;
  onClose: () => void;
  disabled?: boolean; // For unauthenticated users viewing public reports
}

const Sidebar: React.FC<SidebarProps> = ({ isOpen, onClose, disabled = false }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, login } = useAuth();
  const { currentOrganization } = useOrganization();

  const isActive = (path: string) => location.pathname === path;
  const isIdentitiesActive = location.pathname === '/' || location.pathname.startsWith('/identities');
  const isActivityActive = location.pathname === '/activity' || location.pathname.startsWith('/runs');
  const isAdmin = user?.email && ADMIN_EMAILS.includes(user.email.toLowerCase());

  const handleNavigation = (path: string) => {
    onClose();
    navigate(path);
  };

  const itemClass = (active: boolean) =>
    `w-full flex items-center space-x-3 px-3 py-2.5 rounded-lg transition-all duration-150 ${
      disabled
        ? 'text-gray-400 dark:text-slate-600 cursor-not-allowed'
        : active
          ? 'bg-gray-900 dark:bg-blue-600 text-white'
          : 'text-gray-600 dark:text-slate-400 hover:bg-gray-100 dark:hover:bg-slate-800 hover:text-gray-900 dark:hover:text-white'
    }`;

  return (
    <>
      {/* Mobile/Tablet Sidebar Overlay */}
      {isOpen && (
        <div
          className="lg:hidden fixed inset-0 bg-black/30 dark:bg-black/50 z-40"
          onClick={onClose}
        />
      )}

      {/* Sidebar */}
      <div className={`
        fixed lg:static inset-y-0 left-0 z-50
        transform ${isOpen ? 'translate-x-0' : '-translate-x-full'} lg:translate-x-0
        transition-all duration-300 ease-in-out
        h-full
        bg-white dark:bg-slate-900/95
        border-r border-gray-200 dark:border-slate-800
        overflow-y-auto
      `}
      style={{ top: '57.6px', width: '280px' }}>
        <div className="p-5">
          {/* Organization Name */}
          {!disabled && currentOrganization && (
            <div className="mb-6 px-3">
              <div className="flex items-center space-x-3">
                <div className="w-9 h-9 rounded-lg bg-gradient-to-br from-blue-500 to-purple-600 flex items-center justify-center shadow-md">
                  <span className="text-sm font-bold text-white">
                    {currentOrganization.name.charAt(0).toUpperCase()}
                  </span>
                </div>
                <div className="flex-1 min-w-0">
                  <h2 className="text-sm font-semibold text-gray-900 dark:text-white truncate">
                    {currentOrganization.name}
                  </h2>
                  <p className="text-xs text-gray-500 dark:text-slate-500">
                    {currentOrganization.role === 'owner' ? 'Owner' : 'Member'}
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* Main Navigation */}
          <nav className="mb-8">
            <p className="text-[10px] font-semibold text-gray-400 dark:text-slate-500 uppercase tracking-wider mb-3 px-3">
              Menu
            </p>
            <div className="space-y-1">
              {/* Identities */}
              <button
                data-tour="nav-identities"
                onClick={() => !disabled && handleNavigation('/')}
                disabled={disabled}
                className={itemClass(isIdentitiesActive)}
              >
                <svg className="w-5 h-5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M16 12a4 4 0 10-8 0 4 4 0 008 0zm0 0v1.5a2.5 2.5 0 005 0V12a9 9 0 10-9 9m4.5-1.206a8.959 8.959 0 01-4.5 1.207" />
                </svg>
                <span className="text-sm font-medium">Identities</span>
              </button>

              {/* Activity (register/login runs across identities) */}
              <button
                data-tour="nav-activity"
                onClick={() => !disabled && handleNavigation('/activity')}
                disabled={disabled}
                className={itemClass(isActivityActive)}
              >
                <svg className="w-5 h-5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M13 10V3L4 14h7v7l9-11h-7z" />
                </svg>
                <span className="text-sm font-medium">Activity</span>
              </button>

              {/* API Keys */}
              <button
                data-tour="nav-api-keys"
                onClick={() => !disabled && handleNavigation('/api-keys')}
                disabled={disabled}
                className={itemClass(isActive('/api-keys'))}
              >
                <svg className="w-5 h-5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                </svg>
                <span className="text-sm font-medium">API Keys</span>
              </button>

              {/* Usage & Billing */}
              <button
                onClick={() => !disabled && handleNavigation('/billing')}
                disabled={disabled}
                className={itemClass(isActive('/billing'))}
              >
                <svg className="w-5 h-5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z" />
                </svg>
                <span className="text-sm font-medium">Usage & Billing</span>
              </button>

              {/* Settings */}
              <button
                onClick={() => !disabled && handleNavigation('/settings')}
                disabled={disabled}
                className={itemClass(isActive('/settings'))}
              >
                <svg className="w-5 h-5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                </svg>
                <span className="text-sm font-medium">Settings</span>
              </button>

              {/* Analytics - Admin Only */}
              {isAdmin && !disabled && (
                <button
                  onClick={() => handleNavigation('/analytics')}
                  className={`w-full flex items-center space-x-3 px-3 py-2.5 rounded-lg transition-all duration-150 ${
                    isActive('/analytics')
                      ? 'bg-gray-900 dark:bg-blue-600 text-white'
                      : 'text-gray-600 dark:text-slate-400 hover:bg-gray-100 dark:hover:bg-slate-800 hover:text-gray-900 dark:hover:text-white'
                  }`}
                >
                  <svg className="w-5 h-5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                  </svg>
                  <span className="text-sm font-medium">Analytics</span>
                </button>
              )}
            </div>
          </nav>

          {/* Divider */}
          <div className="h-px bg-gray-200 dark:bg-slate-800 mb-6" />

          {/* Docs (external) */}
          <a
            href="https://docs.sente.run"
            target="_blank"
            rel="noopener noreferrer"
            onClick={onClose}
            className={itemClass(false)}
          >
            <svg className="w-5 h-5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.746 0 3.332.477 4.5 1.253v13C19.832 18.477 18.246 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
            </svg>
            <span className="text-sm font-medium">Docs</span>
          </a>

          {/* Replay the onboarding walkthrough */}
          {!disabled && (
            <button
              onClick={() => {
                onClose();
                window.dispatchEvent(new Event('sente:tour:start'));
              }}
              className={itemClass(false)}
            >
              <svg className="w-5 h-5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M8.228 9c.549-1.165 2.03-2 3.772-2 2.21 0 4 1.343 4 3 0 1.4-1.278 2.575-3.006 2.907-.542.104-.994.54-.994 1.093m0 3h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <span className="text-sm font-medium">Product tour</span>
            </button>
          )}

          {/* CTA for unauthenticated users */}
          {disabled && (
            <div className="mb-6 px-3">
              <div className="bg-gradient-to-br from-blue-50 to-purple-50 dark:from-blue-900/20 dark:to-purple-900/20 rounded-xl p-4 border border-blue-100 dark:border-blue-800/30">
                <div className="flex items-center space-x-3 mb-3">
                  <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-blue-500 to-purple-600 flex items-center justify-center shadow-lg shadow-blue-500/25">
                    <svg className="w-5 h-5 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M13 10V3L4 14h7v7l9-11h-7z" />
                    </svg>
                  </div>
                  <div>
                    <h3 className="text-sm font-semibold text-gray-900 dark:text-white">Sente</h3>
                    <p className="text-xs text-gray-500 dark:text-slate-400">Accounts for AI agents</p>
                  </div>
                </div>
                <p className="text-xs text-gray-600 dark:text-slate-300 mb-4">
                  Give your AI agents their own email identities.
                </p>
                <button
                  onClick={() => login()}
                  className="w-full flex items-center justify-center space-x-2 px-4 py-2.5 bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 text-white text-sm font-medium rounded-lg transition-all shadow-md hover:shadow-lg"
                >
                  <span>Sign In to Get Started</span>
                  <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M13 7l5 5m0 0l-5 5m5-5H6" />
                  </svg>
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export default Sidebar;
